/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/

import * as _ from 'lodash';

import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output } from '@angular/core';

import { ConfigService } from '../../../core/services/config.service';
import { PopupService } from '../../../core/services/popup.service';
import { StaticDataStoreService } from './../../../core/services/static-datastore.service';
import { Tab } from './../../../core/model/tab.model';
import { TerminalCommonInfo } from './../../../core/model/terminal-common-info.model';
import { TerminalConfigService } from './../../shared/services/terminal-config.service';

/**
 * @export
 * @class LmsTerminalHeaderComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'lms-terminal-handling-header',
  templateUrl: './lms-terminal-handling-header.component.html',
  styleUrls: ['./lms-terminal-handling-header.component.scss']
})
export class LmsTerminalHandlingHeaderComponent implements OnDestroy, OnChanges {
  @Input() subTypes: Array<string>;
  @Input() tab: Tab;
  summary: TerminalCommonInfo;
  @Output() terminalHandlingSearchText = new EventEmitter();
  @Output() download = new EventEmitter();
  tabLevel: number;
  terminalNAIcon: string;
  thpSearchText: string;
  private defaultLocoInitial = 'CSXT';

  /**
   * Creates an instance of LmsTerminalHandlingHeaderComponent.
   * @param {TerminalConfigService} terminalConfigService
   * @param {ConfigService} configService
   * @param {StaticDataStoreService} staticDataStoreService
   * @param {PopupService} popupService
   *
   * @memberOf LmsTerminalHandlingHeaderComponent
   */
  constructor(private terminalConfigService: TerminalConfigService,
    private configService: ConfigService,
    private staticDataStoreService: StaticDataStoreService,
    private popupService: PopupService) {
    this.tabLevel = this.configService.getConfigData().tabIndex.subTabs;
    this.terminalNAIcon = this.configService.getConfigData().images.topAction.terminal.terminalNAIcon;
  }

  /**
   * Set search parameter and emit from header component.
   * @memberOf LmsTerminalHandlingHeaderComponent
   */
  updateTerminalHandlingSearch() {
    if (this.thpSearchText) {
      this.thpSearchText = this.thpSearchText.trim();
    }
    this.thpSearchText = this.formatSearchParam(this.thpSearchText);
    this.terminalHandlingSearchText.emit(this.thpSearchText);
  }

  /**
   * Invoked when click for clear terminal handling search.
   * @memberOf LmsTerminalHandlingHeaderComponent
   */
  clearTerminalHandlingSearch() {
    this.thpSearchText = '';
    this.terminalHandlingSearchText.emit(this.thpSearchText);
  }

  /**
   * Invoked when changes in initialization.
   * @memberOf LmsTerminalHandlingHeaderComponent
   */
  ngOnChanges() {
    const commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
    if (commonopsPicklist && commonopsPicklist.setOfTerminals) {
      const terminalCommonInfoArr = commonopsPicklist.setOfTerminals;
      if (terminalCommonInfoArr && this.tab.tabSearchParameter && this.tab.tabSearchParameter.searchText) {
        this.summary = _.find(terminalCommonInfoArr,
          { 'terminalMnemonic': this.tab.tabSearchParameter.searchText.trim().toUpperCase() });
        if (this.summary) {
          if (this.summary.city) {
            this.summary.city = this.summary.city.trim();
          }
          if (commonopsPicklist.masterData && commonopsPicklist.masterData.divisonList) {
            const divisionArr = commonopsPicklist.masterData.divisonList;
            if (divisionArr && this.summary) {
              const divisionObj: TerminalCommonInfo = _.find(divisionArr, { 'divisionCode': this.summary.divisionCode });
              if (divisionObj) {
                this.summary.divisionName = divisionObj.divisionName;
              }
            }
          }
        }
      }
    }
  }


  /**
   * Destroy the state of lifecycle hook.
   * @memberOf LmsTerminalHandlingHeaderComponent
   */
  ngOnDestroy() {
  }

  /**
  * Format the locomotive id
  * @private
  * @param {string} value
  * @returns {string}
  * @memberOf LmsTerminalHeaderComponent
  */
  private formatSearchParam(value: string): string {
    let formattedValue = value;
    if (value) {
      value = value.trim();
      if (this.isNumeric(value.charAt(0))) {
        formattedValue = this.defaultLocoInitial + ' ' + value.replace(/^0+/, '');
      } else {
        const v = value.match(/\d+/);
        if (v) {
          const charVal = value.substring(0, v.index);
          if (charVal && charVal.trim().length >= 2 && charVal.trim().length <= 4) {
            const locoInitial = (v.index <= 4) ? value.substring(0, v.index).trim() : value.substring(0, 4).trim();
            const locoNumber = (v.index <= 4) ?
              value.substring(v.index, value.length).trim() : value.substring(4, value.length).trim();
            if (this.isNumeric(locoNumber)) {
              formattedValue = locoInitial + ' ' + locoNumber.replace(/^0+/, '');
            }
          }
        }
      }
    }
    return formattedValue;
  }
  /**
     * To check whether string is a number or not
     * @private
     * @param {string} value
     * @returns {boolean}
     * @memberOf LmsTerminalHeaderComponent
     */
  private isNumeric(value: string): boolean {
    const v = value.match(/^\d+$/);
    if (v) {
      if (v.toString() === value) {
        return true;
      }
    }
    return false;
  }

  /**
   * Invoked for set page mode.
   * @memberOf LmsTerminalHandlingHeaderComponent
   */
  setPageMode() {
    this.popupService.setPageMode('Edit');
    this.popupService.setSubmitBtnId('thpHandlingSearchBtn');
  }

  /**
   * Invoked when reset the page mode.
   * @memberOf LmsTerminalHandlingHeaderComponent
   */
  resetPageMode() {
    this.popupService.setPageMode('');
    this.popupService.setSubmitBtnId('');
  }

  /**
   * Download Excel Method
   * @memberof LmsTerminalHandlingHeaderComponent
   */
  downloadExcel() {
    this.download.emit();
  }

  /**
   * Invoked when click on Backspace/Delete in search box in terminal handling.
   * @param {any} event
   * @memberOf LmsTerminalHandlingHeaderComponent
   */
  @HostListener('window:keydown', ['$event']) defaultEnterFunctionality(event) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      const thpSearchTextBox = document.getElementById('thpSearchTextBox');
      if (thpSearchTextBox && thpSearchTextBox === document.activeElement) {
        const thpClearBtn = document.getElementById('thpClearBtn');
        if (thpClearBtn) {
          thpClearBtn.click();
        }
      }
    }
  }
}
